import React, { useState, useEffect, useContext } from 'react'
import { useNavigate } from "react-router-dom";
import { Form, Button, Row, Table, InputGroup } from "react-bootstrap";
import { ToastContainer } from 'react-toastify';
import axiosInstance from '../../utils/axios';
import { formatDisplayDate } from '../../utils/formatter';
import ModalDelete from '../reusables/ModalDelete';
import AuthContext from '../../context/AuthContext';
import { showToast } from '../reusables/CommonFunction';
import CustomPagination from '../reusables/CustomPagination';
import BusyLoaderOverlay from '../reusables/BusyLoaderOverlay';


const PatientList = () => {
    let { userDetails } = useContext(AuthContext)
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isModalOpen, setIsModalOpen] = useState({});
    const [patientDetails, setPatientDetails] = useState([])
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = parseInt(process.env.REACT_APP_PAGINATION_PAGE_SIZE);
    const salutation = { '1': 'DR.', '2': 'MR.', '3': 'MRS.', '4': 'MISS', '5': 'MASTER' }

    const getPatientDetails = (pageNumber) => {
        setLoading(true);
        axiosInstance
            .get(`/patients/`, {
                params: {
                    q: searchQuery,
                    clinicId: userDetails.clinic_id,
                    page: pageNumber
                }
            })
            .then(response => {
                if (response.status === 200) {
                    setPatientDetails(response.data.data)
                    setTotalCount(response.data.count);
                }
            })
            .catch(error => {
                showToast("error", "Failed to fetch patient details");
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const onPressSearchPatient = (oEvent) => {
        oEvent.preventDefault();
        setCurrentPage(1); // Reset to the first page
        getPatientDetails(1);
    }

    const onPressEditPatient = (oEvent, pPatientId) => {
        oEvent.preventDefault();
        navigate(`/patient/${pPatientId}`)
    }

    const onPressDeletePatient = (oEvent, pPatientId) => {
        oEvent.preventDefault();
        setIsModalOpen({ ['isModalOpen_' + pPatientId]: true })
    }

    const onPressSubmitDeletePatient = (oEvent, pPatientId) => {
        oEvent.preventDefault();
        setLoading(true);
        axiosInstance
            .delete(`/patients/${pPatientId}/`)
            .then(response => {
                if (response.status === 200) {
                    showToast("success", response.data.message);
                    setIsModalOpen({ ['isModalOpen_' + pPatientId]: false })
                    setCurrentPage(1); // Reset to the first page
                    getPatientDetails(1)
                } else if (response.status === 403 && response.data.detail === "Your clinic plan has expired.") {
                    showToast("error", "You cannot delete patient details as your clinic plan has expired. Please renew your plan to manage patients.");
                }
            })
            .catch(error => {
                if (error.response && error.response.status === 403 && error.response.data.detail === "Your clinic plan has expired.") {
                    showToast("error", "You cannot delete patient details as your clinic plan has expired. Please renew your plan to manage patients.");
                } else {
                    showToast("error", "Failed to delete patient details");
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        getPatientDetails(currentPage)
    }, [currentPage]);

    return (
        <Row className="ml-3 mr-3 mb-3">
            <div className='col-md-12'>
                <InputGroup className="mb-3 col-md-4 pl-0" >
                    <Form.Control
                        type="text"
                        name="searchQuery"
                        onChange={(oEvent) => setSearchQuery(oEvent.target.value)}
                        placeholder="Search by Patient Name or Case Number..."
                        value={searchQuery}
                    />
                    <Button type="submit" variant="outline-secondary" onClick={(oEvent) => onPressSearchPatient(oEvent)}>
                        Search
                    </Button>
                </InputGroup>

                {/* If the count is 0 then hide the table */}
                {patientDetails.length > 0 ?
                    <>
                        <Table bordered hover responsive>
                            <thead light="true">
                                <tr>
                                    <th style={{ width: "2rem" }}>#</th>
                                    <th style={{ width: "13rem" }}>Patient Name</th>
                                    <th style={{ width: "9rem" }}>Case Number</th>
                                    <th style={{ width: "10rem" }}>Clinic Branch</th>
                                    <th style={{ width: "6rem" }}>Visit Date</th>
                                    <th style={{ width: "6rem" }}>Last Follow Up</th>
                                    <th style={{ width: "6rem" }}>Next Follow Up</th>
                                    <th style={{ width: "7rem" }} className='d-none d-md-table-cell'>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {patientDetails.map((patient, index) => {
                                    return [
                                        <tr key={patient.patient_id}>
                                            <td>{index + 1}</td>
                                            <td className='text-left'>{salutation[patient.salutation]} {patient.patient_name}</td>
                                            <td className='text-left'>{patient.patient_case_number}</td>
                                            <td className='text-left'>{patient.branch_name}</td>
                                            <td className='text-left'>{formatDisplayDate(patient.visit_date)}</td>
                                            <td className='text-left'>{formatDisplayDate(patient.last_follow_up_date)}</td>
                                            <td className='text-left'>{formatDisplayDate(patient.next_appointment_date)}</td>
                                            <td className='d-none d-md-table-cell'>
                                                <Row>
                                                    <div className='col-6'>
                                                        <Button type='button' variant='info' size='sm' onClick={oEvent => onPressEditPatient(oEvent, patient.patient_id)} >Edit</Button>
                                                    </div>
                                                    {userDetails.is_clinic_plan_expired === false ? (
                                                        <div className='col-6 pl-0'>
                                                            <Button type='button' variant='danger' size='sm' disabled={userDetails.user_type === "M"} onClick={oEvent => onPressDeletePatient(oEvent, patient.patient_id)}>Delete</Button>
                                                            <ModalDelete show={isModalOpen['isModalOpen_' + patient.patient_id]} modalTitle="Patient" modalBody={patient.patient_name}
                                                                onHide={() => setIsModalOpen({ ['isModalOpen_' + patient.patient_id]: false })}
                                                                onSubmit={oEvent => onPressSubmitDeletePatient(oEvent, patient.patient_id)} />
                                                        </div>
                                                    ) : null}
                                                </Row>
                                            </td>
                                        </tr>
                                    ]
                                })}
                            </tbody>
                        </Table>

                        <CustomPagination
                            totalCount={totalCount}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            onPageChange={setCurrentPage} />

                    </>
                    : <> </>}
                <ToastContainer />
                <BusyLoaderOverlay show={loading} />
            </div>
        </Row>
    )

}

export default PatientList